import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { CardHeader } from 'components/cards';
import { FilterBar, useFilterBar } from 'components/filter';
import Page from 'components/layout/Page/Page';
import DeleteModal from 'components/layout/modals/DeleteModal/DeleteModal';
import { TextButton } from 'components/buttons';
import { StackedList } from 'components/stackedLists';
import {
  NOTIFICATION_CHANNEL_FILTER_OPTIONS,
  NOTIFICATION_READ_FILTER_OPTIONS,
  NOTIFICATION_TYPE_FILTER_OPTIONS,
  NOTIFICATIONS_PAGE_SIZE,
} from 'constants/notifications';
import {
  deleteNotification,
  fetchNotifications,
  fetchUnreadNotificationCount,
  markAllNotificationsRead,
  markNotificationRead,
  markNotificationUnread,
  selectAllNotifications,
  selectNotificationPendingDeletes,
  selectNotificationPendingReads,
  selectNotificationsFilters,
  selectNotificationsInitialFetchDone,
  selectNotificationsLoading,
  selectNotificationsPageInfo,
  selectUnreadNotificationCount,
  setFilters,
} from 'store/slices/notificationsSlice';
import { selectSettings } from 'store/slices/authSlice';
import { getNotificationTarget, serializeNotificationReadFilter } from 'utils/notifications';
import NotificationListItem from './components/NotificationListItem/NotificationListItem';

const NOTIFICATION_LIST_FILTERS = [
  {
    key: 'search',
    type: 'search',
    label: 'Search',
    placeholder: 'Search notifications',
    tier: 'primary',
  },
  {
    key: 'type',
    type: 'select',
    label: 'Type',
    options: NOTIFICATION_TYPE_FILTER_OPTIONS,
    tier: 'primary',
  },
  {
    key: 'channel',
    type: 'select',
    label: 'Channel',
    options: NOTIFICATION_CHANNEL_FILTER_OPTIONS,
    tier: 'primary',
  },
  {
    key: 'readState',
    type: 'select',
    label: 'Read state',
    options: NOTIFICATION_READ_FILTER_OPTIONS,
    tier: 'primary',
  },
];

const NOTIFICATION_LIST_FILTER_CONFIG = {
  route: '/notifications',
  filters: {
    search: {
      type: 'search',
      urlKey: 'search',
      default: '',
    },
    type: {
      type: 'select',
      urlKey: 'type',
      default: 'ALL',
      options: NOTIFICATION_TYPE_FILTER_OPTIONS,
    },
    channel: {
      type: 'select',
      urlKey: 'channel',
      default: 'ALL',
      options: NOTIFICATION_CHANNEL_FILTER_OPTIONS,
    },
    readState: {
      type: 'select',
      urlKey: 'isRead',
      default: 'ALL',
      options: NOTIFICATION_READ_FILTER_OPTIONS,
      serialize: serializeNotificationReadFilter,
    },
  },
  pagination: {
    pageSize: NOTIFICATIONS_PAGE_SIZE,
  },
  fetchThunk: fetchNotifications,
  setFilters,
  selectFilters: selectNotificationsFilters,
  selectLoading: selectNotificationsLoading,
  selectInitialFetchDone: selectNotificationsInitialFetchDone,
};

export default function Notifications() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const notifications = useSelector(selectAllNotifications);
  const pageInfo = useSelector(selectNotificationsPageInfo);
  const loading = useSelector(selectNotificationsLoading);
  const settings = useSelector(selectSettings);
  const unreadCount = useSelector(selectUnreadNotificationCount);
  const pendingDeletesById = useSelector(selectNotificationPendingDeletes);
  const pendingReadsById = useSelector(selectNotificationPendingReads);
  const [notificationToDelete, setNotificationToDelete] = useState(null);

  const {
    filterValues,
    isInitialLoading,
    hasActiveFilters,
    applyFilters,
    resetFilters,
    handlePageChange,
    refetchCurrentPage,
  } = useFilterBar(NOTIFICATION_LIST_FILTER_CONFIG);

  useEffect(() => {
    dispatch(fetchUnreadNotificationCount());
  }, [dispatch]);

  const handleDeleteClose = useCallback(() => {
    setNotificationToDelete(null);
  }, []);

  const handleMarkRead = useCallback(async (notification) => {
    if (notification?.isRead) {
      return;
    }

    try {
      await dispatch(markNotificationRead(notification._id)).unwrap();
    } catch (_error) {
      // Redux keeps the error state.
    }
  }, [dispatch]);

  const handleMarkUnread = useCallback(async (notification) => {
    if (!notification?.isRead) {
      return;
    }

    try {
      await dispatch(markNotificationUnread(notification._id)).unwrap();
    } catch (_error) {
      // Redux keeps the error state.
    }
  }, [dispatch]);

  const handleOpen = useCallback(async (notification) => {
    await handleMarkRead(notification);
    const target = getNotificationTarget(notification);
    navigate(target.path, { state: { from: '/notifications' } });
  }, [handleMarkRead, navigate]);

  const handleAction = useCallback(async (action, notification) => {
    switch (action) {
      case 'read':
        await handleMarkRead(notification);
        break;
      case 'open':
        await handleOpen(notification);
        break;
      case 'mark-read':
        await handleMarkRead(notification);
        break;
      case 'mark-unread':
        await handleMarkUnread(notification);
        break;
      case 'delete':
        setNotificationToDelete(notification);
        break;
      default:
        break;
    }
  }, [handleMarkRead, handleMarkUnread, handleOpen]);

  const handleConfirmDelete = useCallback(async () => {
    if (!notificationToDelete) {
      return;
    }

    try {
      await dispatch(deleteNotification(notificationToDelete._id)).unwrap();
      handleDeleteClose();
      refetchCurrentPage();
    } catch (_error) {
      // Redux keeps the error state.
    }
  }, [dispatch, handleDeleteClose, notificationToDelete, refetchCurrentPage]);

  const handleMarkAllRead = useCallback(async () => {
    try {
      await dispatch(markAllNotificationsRead()).unwrap();
    } catch (_error) {
      // Redux keeps the error state.
    }
  }, [dispatch]);

  const getIsItemUpdating = useCallback((item) => (
    Boolean(pendingDeletesById[item._id] || pendingReadsById[item._id])
  ), [pendingDeletesById, pendingReadsById]);

  const rightAction = useMemo(() => (
    unreadCount > 0 ? (
      <TextButton onClick={handleMarkAllRead}>
        Mark all as read
      </TextButton>
    ) : null
  ), [handleMarkAllRead, unreadCount]);

  return (
    <Page title="Notifications">
      <CardHeader
        leftAction={
          <FilterBar
            filters={NOTIFICATION_LIST_FILTERS}
            values={filterValues}
            onChange={applyFilters}
            onReset={resetFilters}
            hasActiveFilters={hasActiveFilters}
            loading={isInitialLoading}
          />
        }
        rightAction={rightAction}
      >
        <StackedList
          items={notifications}
          settings={settings}
          onAction={handleAction}
          itemComponent={NotificationListItem}
          emptyMessage="No notifications found."
          getIsItemUpdating={getIsItemUpdating}
          pagination={pageInfo ? {
            pageIndex: pageInfo.page - 1,
            pageSize: pageInfo.limit,
            totalItems: pageInfo.totalDocs,
            onPageChange: handlePageChange,
          } : undefined}
          loading={isInitialLoading}
        />
      </CardHeader>

      <DeleteModal
        isOpen={Boolean(notificationToDelete)}
        onClose={handleDeleteClose}
        title="Delete Notification"
        message={`Are you sure you want to delete notification "${notificationToDelete?.title || 'Untitled notification'}"? This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
        loading={Boolean(notificationToDelete && pendingDeletesById[notificationToDelete._id]) || loading}
      />
    </Page>
  );
}
